import { useState } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { Download, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { buildReportParams, REPORTS, type ReportFilterValues } from '@/features/reports/config'
import { downloadFile } from '@/lib/download'
import { useMe } from '@/features/auth/hooks'
import { requireFlow } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/reports/')({
  beforeLoad: requireFlow('reports'),
  component: ReportsPage,
})

function ReportsPage() {
  const { data: me } = useMe()
  // The Reports flow opens the page; each report still needs its source flow
  // (its export hits that module's endpoints, which enforce the flow too).
  const available = REPORTS.filter((r) => me?.flows.includes(r.flow))
  const [reportId, setReportId] = useState(available[0]?.id ?? '')
  const report = available.find((r) => r.id === reportId) ?? available[0]
  const [values, setValues] = useState<ReportFilterValues>({})
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectReport = (id: string) => {
    setReportId(id)
    setValues({})
    setError(null)
  }

  const setValue = (key: string, value: string | boolean | undefined) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setError(null)
  }

  if (!report) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <FileText className="h-8 w-8" /> Reports
          </h1>
          <p className="text-muted-foreground">
            Export your data as CSV for spreadsheets and accounting.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No reports available</CardTitle>
            <CardDescription>
              Reports are built from flow data — ask an admin to grant this role a flow besides
              Reports.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const handleDownload = async () => {
    setPending(true)
    setError(null)
    try {
      const today = new Date().toISOString().slice(0, 10)
      await downloadFile(report.endpoint, {
        params: buildReportParams(report, values),
        filename: `${report.filename}-${today}.csv`,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-8 w-8" /> Reports
        </h1>
        <p className="text-muted-foreground">
          Export your data as CSV for spreadsheets and accounting.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {available.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => selectReport(r.id)}
            className="h-full w-full text-left"
          >
            <Card
              className={
                r.id === report.id
                  ? 'h-full border-primary ring-1 ring-primary'
                  : 'h-full transition-colors hover:border-muted-foreground/40'
              }
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <r.icon className="h-4 w-4" /> {r.label}
                </CardTitle>
                <CardDescription>{r.description}</CardDescription>
              </CardHeader>
            </Card>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{report.label} export</CardTitle>
          <CardDescription>
            Filters apply to the exported rows — leave them empty to export everything.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            {report.searchPlaceholder && (
              <Input
                placeholder={report.searchPlaceholder}
                value={typeof values.q === 'string' ? values.q : ''}
                onChange={(e) => setValue('q', e.target.value)}
                className="w-full sm:max-w-sm"
              />
            )}
            {report.filters.map((filter) => {
              if (filter.kind === 'select') {
                return (
                  <Select
                    key={filter.param}
                    value={(values[filter.param] as string) ?? 'all'}
                    onValueChange={(v) => setValue(filter.param, v)}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder={filter.label} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All {filter.label.toLowerCase()}</SelectItem>
                      {filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
              if (filter.kind === 'daterange') {
                const from = values[filter.fromParam]
                const to = values[filter.toParam]
                return (
                  <div key={filter.label} className="flex items-center gap-2">
                    <Label className="font-normal text-muted-foreground">{filter.label}</Label>
                    <Input
                      type="date"
                      aria-label={`${filter.label} from`}
                      value={typeof from === 'string' ? from : ''}
                      onChange={(e) => setValue(filter.fromParam, e.target.value)}
                      className="w-[150px]"
                    />
                    <span className="text-sm text-muted-foreground">–</span>
                    <Input
                      type="date"
                      aria-label={`${filter.label} to`}
                      value={typeof to === 'string' ? to : ''}
                      onChange={(e) => setValue(filter.toParam, e.target.value)}
                      className="w-[150px]"
                    />
                  </div>
                )
              }
              return (
                <div key={filter.param} className="flex items-center gap-2">
                  <Checkbox
                    id={`filter-${filter.param}`}
                    checked={values[filter.param] === true}
                    onCheckedChange={(checked) => setValue(filter.param, checked === true)}
                  />
                  <Label htmlFor={`filter-${filter.param}`} className="font-normal">
                    {filter.label}
                  </Label>
                </div>
              )
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleDownload} disabled={pending}>
              <Download className="mr-2 h-4 w-4" />
              {pending ? 'Preparing…' : 'Download CSV'}
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
